import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  getWaitingRoomByDoctorApi,
  fetchWaitingRoomPatientsApi,
  pushPatientToWaitingRoomApi,
  removePatientFromWaitingRoomApi,
} from '../services/waitingRoomService';
import { useBroadcastListener } from '../services/broadcast/broadcastService';

// Format patient object consistently
export const formatWaitingPatient = (patient) => ({
  id: patient.id,
  name: `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.name || 'Unknown Patient',
  time: patient.time || new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  }),
  status: patient.status || 'waiting',
  ...patient,
});

// Waiting room state is event-driven via real-time WebSocket/Echo events
// (.patient.added, .patient.removed) and optimistic mutations.
// Expanded staleTime avoids redundant short polling while real-time listeners keep cache accurate.
export const WAITING_ROOM_STALE_TIME = 15 * 60 * 1000; // 15 minutes
export const WAITING_ROOM_GC_TIME = 30 * 60 * 1000;    // 30 minutes

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch waiting room data for a doctor
 */
export function useWaitingRoomByDoctor(doctorId, options = {}) {
  return useQuery({
    queryKey: queryKeys.waitingRoom.byDoctor(doctorId),
    queryFn: async ({ signal }) => {
      const response = await getWaitingRoomByDoctorApi({ doctorId, signal });
      const roomData = response?.data?.data ?? response?.data ?? null;

      if (!roomData) return null;

      const rawPatients = roomData.patients || [];
      const formattedPatients = rawPatients.map(formatWaitingPatient);

      return {
        ...roomData,
        patients: formattedPatients,
      };
    },
    enabled: Boolean(doctorId),
    staleTime: WAITING_ROOM_STALE_TIME,
    gcTime: WAITING_ROOM_GC_TIME,
    refetchOnWindowFocus: false,
    ...options,
  });
}

/**
 * Fetch patients for a specific waiting room ID
 */
export function useWaitingRoomPatients(roomId, options = {}) {
  return useQuery({
    queryKey: queryKeys.waitingRoom.patients(roomId),
    queryFn: async ({ signal }) => {
      const response = await fetchWaitingRoomPatientsApi({ roomId, signal });
      const rawPatients = response?.data?.data ?? response?.data?.patients ?? response?.data ?? [];
      return Array.isArray(rawPatients) ? rawPatients.map(formatWaitingPatient) : [];
    },
    enabled: Boolean(roomId),
    staleTime: WAITING_ROOM_STALE_TIME,
    gcTime: WAITING_ROOM_GC_TIME,
    refetchOnWindowFocus: false,
    ...options,
  });
}

// ============================================================================
// Mutation Hooks (with Optimistic Updates & Targeted Invalidation)
// ============================================================================

/**
 * Optimistically add a patient to the waiting room
 */
export function usePushPatientToWaitingRoom(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => pushPatientToWaitingRoomApi(variables),
    onMutate: async ({ roomId, doctorId, patient }) => {
      // Snapshot queries to rollback if needed
      const doctorKey = doctorId ? queryKeys.waitingRoom.byDoctor(doctorId) : null;
      const roomPatientsKey = roomId ? queryKeys.waitingRoom.patients(roomId) : null;

      if (doctorKey) await queryClient.cancelQueries({ queryKey: doctorKey });
      if (roomPatientsKey) await queryClient.cancelQueries({ queryKey: roomPatientsKey });

      const prevDoctorData = doctorKey ? queryClient.getQueryData(doctorKey) : null;
      const prevRoomPatients = roomPatientsKey ? queryClient.getQueryData(roomPatientsKey) : null;

      const formatted = formatWaitingPatient(patient || { id: `temp-${Date.now()}` });

      // Optimistic update doctor's waiting room
      if (doctorKey) {
        queryClient.setQueryData(doctorKey, (old) => {
          if (!old) return old;
          const currentPatients = old.patients || [];
          if (currentPatients.some((p) => p.id === formatted.id)) return old;
          return {
            ...old,
            patients: [...currentPatients, formatted],
          };
        });
      }

      // Optimistic update room patients
      if (roomPatientsKey) {
        queryClient.setQueryData(roomPatientsKey, (old = []) => {
          if (old.some((p) => p.id === formatted.id)) return old;
          return [...old, formatted];
        });
      }

      return { prevDoctorData, prevRoomPatients, doctorKey, roomPatientsKey, doctorId, roomId };
    },
    onError: (err, variables, context) => {
      if (context?.doctorKey && context?.prevDoctorData) {
        queryClient.setQueryData(context.doctorKey, context.prevDoctorData);
      }
      if (context?.roomPatientsKey && context?.prevRoomPatients) {
        queryClient.setQueryData(context.roomPatientsKey, context.prevRoomPatients);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.doctorKey) {
        queryClient.invalidateQueries({ queryKey: context.doctorKey });
      }
      if (context?.roomPatientsKey) {
        queryClient.invalidateQueries({ queryKey: context.roomPatientsKey });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

/**
 * Optimistically remove a patient from the waiting room
 */
export function useRemovePatientFromWaitingRoom(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables) => removePatientFromWaitingRoomApi(variables),
    onMutate: async ({ roomId, doctorId, patient_id }) => {
      const doctorKey = doctorId ? queryKeys.waitingRoom.byDoctor(doctorId) : null;
      const roomPatientsKey = roomId ? queryKeys.waitingRoom.patients(roomId) : null;

      if (doctorKey) await queryClient.cancelQueries({ queryKey: doctorKey });
      if (roomPatientsKey) await queryClient.cancelQueries({ queryKey: roomPatientsKey });

      const prevDoctorData = doctorKey ? queryClient.getQueryData(doctorKey) : null;
      const prevRoomPatients = roomPatientsKey ? queryClient.getQueryData(roomPatientsKey) : null;

      // Optimistic remove from doctor waiting room
      if (doctorKey) {
        queryClient.setQueryData(doctorKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            patients: (old.patients || []).filter((p) => p.id !== patient_id),
          };
        });
      }

      // Optimistic remove from room patients
      if (roomPatientsKey) {
        queryClient.setQueryData(roomPatientsKey, (old = []) => {
          return old.filter((p) => p.id !== patient_id);
        });
      }

      return { prevDoctorData, prevRoomPatients, doctorKey, roomPatientsKey };
    },
    onError: (err, variables, context) => {
      if (context?.doctorKey && context?.prevDoctorData) {
        queryClient.setQueryData(context.doctorKey, context.prevDoctorData);
      }
      if (context?.roomPatientsKey && context?.prevRoomPatients) {
        queryClient.setQueryData(context.roomPatientsKey, context.prevRoomPatients);
      }
      options.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.doctorKey) {
        queryClient.invalidateQueries({ queryKey: context.doctorKey });
      }
      if (context?.roomPatientsKey) {
        queryClient.invalidateQueries({ queryKey: context.roomPatientsKey });
      }
      options.onSettled?.(data, error, variables, context);
    },
    ...options,
  });
}

// ============================================================================
// Real-time sync
// ============================================================================

/**
 * Keep the waiting-room cache live. The backend broadcasts
 * `patient.waiting-room.added` / `patient.waiting-room.removed` on the shared
 * `doctor.{id}.channel` whenever a patient enters or leaves the room — e.g. when
 * the doctor marks an appointment "present" or "finished" from the consultation
 * (finishing detaches the patient). Applying the fresh room payload straight to
 * the TanStack cache updates every open view (the doctor's other tabs, every
 * nurse of that doctor) without a page reload.
 *
 * `doctorId` is the room owner's id (for a nurse: their `parent_id`).
 */
export function useWaitingRoomRealtimeSync(doctorId) {
  const queryClient = useQueryClient();

  const applyRoomPayload = useCallback(
    (payload) => {
      if (!doctorId) return;
      const room = payload?.waitingRoom ?? payload?.waiting_room ?? payload;
      const rawPatients = room && Array.isArray(room.patients) ? room.patients : null;

      if (rawPatients) {
        const formatted = rawPatients.map(formatWaitingPatient);
        queryClient.setQueryData(queryKeys.waitingRoom.byDoctor(doctorId), (old) => ({
          ...(old || {}),
          ...(room && typeof room === 'object' ? room : {}),
          patients: formatted,
        }));
        if (room?.id) {
          queryClient.setQueryData(queryKeys.waitingRoom.patients(room.id), formatted);
        }
      }

      // Safety net: reconcile anything the payload did not carry.
      queryClient.invalidateQueries({ queryKey: queryKeys.waitingRoom.all });
    },
    [doctorId, queryClient],
  );

  useBroadcastListener({
    channelName: doctorId ? `doctor.${doctorId}.channel` : null,
    isPrivate: true,
    enabled: Boolean(doctorId),
    events: {
      '.patient.waiting-room.added': applyRoomPayload,
      '.patient.waiting-room.removed': applyRoomPayload,
    },
  });
}